/**
 * KAN-788: фильтр по настраиваемому атрибуту лида (KAN-783) для доски CRM
 * (на клиенте) и списка клиентов (параметры запроса attr / attr_value).
 * Фильтр хранит id атрибута; значения в строке CRM лежат по названию
 * (CrmRow.attributes: Record<название, значение | null>).
 */
import type { CrmAttribute, CrmRow } from '@/types/crm';

export interface CrmAttributeFilter {
  /** id атрибута. */
  attr: string;
  /** null: любое заполненное значение. */
  value: string | null;
}

/** Описание атрибута, достаточное для фильтра (CrmAttribute, LeadAttribute). */
export type FilterableAttribute = Pick<CrmAttribute, 'id' | 'name' | 'type' | 'options'>;

const BOOL_OPTIONS: readonly string[] = ['да', 'нет'];

function norm(value: string): string {
  return value.trim().toLowerCase();
}

/** Варианты селектора "Значение": choice из настроек, bool да/нет, text без вариантов. */
export function attributeFilterOptions(attribute: FilterableAttribute): string[] {
  if (attribute.type === 'choice') return [...attribute.options];
  if (attribute.type === 'bool') return [...BOOL_OPTIONS];
  return [];
}

/**
 * Фильтр из сохранённых строк (адрес, localStorage). Нет фильтра (null):
 * пустой или неизвестный id, а для choice и bool ещё и значение вне
 * вариантов (backend списка клиентов отвечает на него 400). Значение
 * choice и bool приводится к написанию варианта.
 */
export function parseAttributeFilter(
  attr: string | null | undefined,
  value: string | null | undefined,
  attributes: readonly FilterableAttribute[]
): CrmAttributeFilter | null {
  if (!attr) return null;
  const attribute = attributes.find((a) => a.id === attr);
  if (!attribute) return null;
  if (!value || !value.trim()) return { attr, value: null };
  if (attribute.type === 'text') return { attr, value };
  const option = attributeFilterOptions(attribute).find((o) => norm(o) === norm(value));
  return option === undefined ? null : { attr, value: option };
}

/**
 * Строки, у которых атрибут фильтра заполнен (value null) или равен value
 * без учёта регистра и крайних пробелов. Фильтра нет или атрибут неизвестен:
 * тот же массив.
 */
export function filterRowsByAttribute<T extends { row: Pick<CrmRow, 'attributes'> }>(
  items: T[],
  filter: CrmAttributeFilter | null,
  attributes: readonly FilterableAttribute[]
): T[] {
  if (!filter) return items;
  const attribute = attributes.find((a) => a.id === filter.attr);
  if (!attribute) return items;
  const wanted = filter.value === null ? null : norm(filter.value);
  return items.filter((item) => {
    const actual = norm(item.row.attributes?.[attribute.name] ?? '');
    if (!actual) return false;
    return wanted === null || actual === wanted;
  });
}
